import React from "react";
import LegalPage from "@/components/legal/LegalPage";

export default function Privacy() {
  return (
    <LegalPage
      pageTitle="Privacy Policy"
      slug="privacy"
      seoDescription="JobHacki privacy policy: what we collect (your email and an auth cookie), the services we run on (Supabase, Google Cloud, Whop, Resend), and how to get your data deleted."
      kicker="PRIVACY POLICY"
      title={<>Short on purpose. <span className="bg-[#0a0a0a] text-[#FFE600] px-2">We collect very little.</span></>}
      effectiveDate="June 11, 2026"
      accent="#FFE600"
      intro={<>JobHacki (&ldquo;we&rdquo;, &ldquo;us&rdquo;) runs jobhacki.com — a membership site with income playbooks and career tools. This page explains exactly what we collect, what we don&apos;t, which services we run on, and how to get your data deleted.</>}
      sections={[
        {
          title: "What we collect",
          body: (
            <ul className="space-y-2 list-disc pl-5">
              <li><b>Your email address</b> — when you sign up for an account. That&apos;s how we identify your account and check your membership status.</li>
              <li><b>An authentication cookie</b> — a cookie named <code className="bg-[#0a0a0a]/10 px-1.5 py-0.5 font-mono-body">jh_at</code> that keeps you logged in (see Cookies below).</li>
              <li><b>Standard server logs</b> — like most websites, our hosting provider records basic request data (IP address, browser type, pages requested) for security and debugging.</li>
            </ul>
          ),
        },
        {
          title: "What we never collect",
          body: (
            <>
              <p><b>We never see or store your card details.</b> All payments are processed by Whop. Card numbers go directly to Whop&apos;s payment infrastructure — they never touch our servers. We only learn whether your membership is active.</p>
              <p>We also don&apos;t run advertising trackers, and we don&apos;t sell or rent your personal information to anyone.</p>
            </>
          ),
        },
        {
          title: "The services we run on",
          body: (
            <>
              <p>JobHacki is built on a small set of providers, each of which handles a specific piece of your data under its own privacy policy:</p>
              <ul className="space-y-2 list-disc pl-5">
                <li><b>Supabase</b> — authentication and our database. Your email and account record live here.</li>
                <li><b>Google Cloud</b> — hosting and file storage. Serves the site and its assets; produces the standard server logs above.</li>
                <li><b>Whop</b> — payment processing for paid memberships. Handles all billing data, including card details.</li>
                <li><b>Resend</b> — transactional email. Used to send account-related emails (e.g. sign-in and account notices) to the address you signed up with.</li>
              </ul>
            </>
          ),
        },
        {
          title: "Cookies",
          body: (
            <p>We set one cookie: <b>jh_at</b>, an authentication cookie that holds your login session so you stay signed in between visits. It exists purely so the site knows it&apos;s you. We do not use advertising or cross-site tracking cookies.</p>
          ),
        },
        {
          title: "How we use your information",
          body: (
            <ul className="space-y-2 list-disc pl-5">
              <li>To create and maintain your account.</li>
              <li>To check whether your membership is active and unlock the tools you have access to.</li>
              <li>To send account-related (transactional) email.</li>
              <li>To keep the site secure and fix problems.</li>
              <li>That&apos;s it. We don&apos;t use your data for advertising and we don&apos;t share it beyond the providers listed above.</li>
            </ul>
          ),
        },
        {
          title: "Data retention and deletion",
          body: (
            <p>We keep your account data for as long as your account exists. Want it gone? Email <a className="font-bold border-b-2 border-current" href="mailto:support@jobhacki.com">support@jobhacki.com</a> from the address on your account and ask for deletion. We&apos;ll delete your account record and confirm when it&apos;s done. Note that Whop retains billing records under its own policy as required for payment processing.</p>
          ),
        },
        {
          title: "Your rights (GDPR / CCPA)",
          body: (
            <>
              <p>Depending on where you live (including the EU/EEA under GDPR and California under CCPA/CPRA), you have the right to:</p>
              <ul className="space-y-2 list-disc pl-5">
                <li><b>Access</b> — ask what personal data we hold about you (for most members: an email address).</li>
                <li><b>Correction</b> — ask us to fix inaccurate data.</li>
                <li><b>Deletion</b> — ask us to delete your data (see above).</li>
                <li><b>Portability</b> — receive a copy of your data in a usable format.</li>
                <li><b>Opt out of sale or sharing</b> — we don&apos;t sell or share personal information for advertising, so there&apos;s nothing to opt out of, but the right is yours regardless.</li>
                <li><b>Non-discrimination</b> — exercising any of these rights will never affect your access or pricing.</li>
              </ul>
              <p>To exercise any of these rights, email <a className="font-bold border-b-2 border-current" href="mailto:support@jobhacki.com">support@jobhacki.com</a>. We&apos;ll respond within the timelines the applicable law requires.</p>
            </>
          ),
        },
        {
          title: "Children",
          body: (
            <p>JobHacki is not directed at children under 16, and we don&apos;t knowingly collect data from them. If you believe a child has created an account, email us and we&apos;ll delete it.</p>
          ),
        },
        {
          title: "Changes to this policy",
          body: (
            <p>If we change this policy, we&apos;ll update the effective date at the top of this page. Material changes will be flagged on the site.</p>
          ),
        },
      ]}
    />
  );
}
